import firebase from "./firebase";
import "firebase/compat/auth";
import { handleFirebaseAuthError } from "./firebaseErrorHandler";
import { axiosGraphQL } from "../api";

export const getStorage = () => {
  return localStorage;
};

/**
 * Properly Check if User is Logged In
 */
export const isUserLoggedIn = async () => {
  return new Promise((resolve) => {
    firebase.auth().onAuthStateChanged((user) => {
      resolve(!!user); // Ensures we wait for Firebase to initialize
    });
  });
};

export const getUserData = () =>
  getStorage().getItem("user")
    ? JSON.parse(getStorage().getItem("user"))
    : null;

/**
 * Send Sign-In Link to Email (Passwordless)
 */
export const sendSignInLink = async (email, language) => {
  const query = `
    mutation SendMagicLink($email: String!, $language: String, $isLocal: Boolean) {
      sendMagicLink(email: $email, language: $language, isLocal: $isLocal)
    }
  `;

  const isLocal =
    process.env.REACT_APP_NODE_ENV === "development" &&
    window.location.hostname.includes("localhost");

  try {
    await axiosGraphQL(
      {
        query,
        variables: isLocal
          ? { email, language, isLocal }
          : {
              email,
              language,
            },
      },
      { noAuth: true } // skip token-based auth logic
    );

    getStorage().setItem("emailForSignIn", email); // Save to complete login later
    const storedEmail = getStorage().getItem("emailForSignIn");

    if (!storedEmail || storedEmail !== email) {
      throw new Error("Failed to store email. Try again.");
    }

    return true;
  } catch (error) {
    console.error("Error sending magic link via backend:", error.message);
    throw new Error("Failed to send login link. Please try again.");
  }
};

/**
 * Completes Firebase passwordless sign-in from magic link
 */
export const completeSignInWithEmailLink = async () => {
  try {
    const currentUrl = window.location.href;

    if (!firebase.auth().isSignInWithEmailLink(currentUrl)) {
      throw new Error("Invalid or expired login link.");
    }

    const email = getStorage().getItem("emailForSignIn");

    if (!email) {
      // If email is missing, redirect user back with an error
      const message = "Your login session has expired. Please try again.";
      window.location.href = `/login?error=${encodeURIComponent(message)}`;
      return;
    }

    const result = await firebase.auth().signInWithEmailLink(email, currentUrl);
    const user = result.user;

    // Clean up
    getStorage().removeItem("emailForSignIn");

    // Get fresh ID token
    const idToken = await user.getIdToken(true);

    // Store auth token and user data in localStorage
    getStorage().setItem("token", idToken);
    getStorage().setItem("tokenLastValidity", Date.now().toString());
    getStorage().setItem("user", JSON.stringify(user));

    // Handle optional redirect parameter
    const params = new URLSearchParams(window.location.search);
    const redirect = params.get("redirect") || "/";

    // Clean up URL (remove Firebase query params)
    window.history.replaceState({}, document.title, redirect);

    return { user, idToken };
  } catch (error) {
    console.error("Error completing sign-in:", error.message);
    throw new Error(handleFirebaseAuthError(error));
  }
};

/**
 * Sign out the user
 */
export const signOut = async () => {
  try {
    await firebase.auth().signOut();
    console.log("Successfully signed out");
    clearStorage();
    return true;
  } catch (error) {
    console.error("Error signing out:", error);
    throw new Error("Unable to sign out");
  }
};

/**
 * Get Firebase Authenticated User
 */
export const getFirebaseUser = () => {
  return new Promise((resolve, reject) => {
    firebase.auth().onAuthStateChanged((user) => {
      if (user) {
        process.env.REACT_APP_NODE_ENV === "development" &&
          console.log(
            "[utils.js - getFirebaseUser] user => ",
            JSON.stringify(user)
          );
        resolve(user);
      } else {
        reject();
      }
    });
  });
};

/**
 * Clear local/session storage and cookies
 */
export const clearCookies = () => {
  document.cookie.split(";").forEach((c) => {
    document.cookie = c
      .replace(/^ +/, "")
      .replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
  });
};

export const clearStorage = (appKeys = ["user", "token"]) => {
  appKeys.forEach((key) => localStorage.removeItem(key));
};
